/*
 * The trail above the screen: the fleet's `BreadcrumbsBase` (the reference CMS design @
 * `c4387239`) with its own data half replaced, and the reason is what a crumb
 * has to point at here.
 *
 * The fleet reads `staticData.crumb` off each match and links to the match's
 * own pathname, which works because every one of its addresses is complete. Two
 * of ours are not: a site is addressed without its workspace (`/sites/$siteId`,
 * design note 0001 §2), so the step above it has to be filled in from what the
 * screen published, and the crumb for a site is the site's name — which no
 * route knows. `lib/sidebar-items.ts` holds that walk; this file draws it.
 *
 * `BreadcrumbsBase` below is the fleet's, unchanged.
 */
import React, { Suspense, useMemo } from 'react'
import { Link, useRouter } from '@tanstack/react-router'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { breadcrumbTrail, type DeclaredRoute } from '@/lib/sidebar-items'
import { usePageFrameValue } from '@/components/layout/page-frame'
import { useMatches } from '@tanstack/react-router'

/*
 * Every route the router was built from, by id.
 *
 * `routesById` and not `flatRoutes`, which is what the fleet's own
 * `useDynamicSidebar` reads: in this version `flatRoutes` is filled in when the
 * router first loads, so a component that asks during the first render gets
 * `undefined` — and a trail derived from nothing is an empty trail, which looks
 * like a screen that simply has no breadcrumbs. `routesById` is there from the
 * moment the router is made. The cast is because neither is in the exported
 * type here; only what is read is asserted.
 */
type FlatRoute = { id: string; fullPath: string; options?: { staticData?: DeclaredRoute["staticData"] } }

const flatRoutes = (router: unknown): FlatRoute[] =>
  Object.values((router as { routesById?: Record<string, FlatRoute> }).routesById ?? {})

export const Breadcrumbs = () => {
  const router = useRouter()
  const matches = useMatches()
  const frame = usePageFrameValue()

  const items = useMemo(() => {
    /*
     * The routes as declared, from the router rather than from an import of the
     * tree: this file is part of the frame the tree renders, and importing it
     * back would be a cycle.
     */
    const routes: DeclaredRoute[] = flatRoutes(router).map((route) => ({
      id: route.id,
      to: route.fullPath,
      staticData: route.options?.staticData,
    }))

    const current = matches.at(-1)
    if (!current) return []

    // What the address holds, plus the workspace a screen published when its
    // own path does not carry one.
    const params = frame.workspaceId
      ? { ...(current.params as Record<string, string | undefined>), workspaceId: frame.workspaceId }
      : (current.params as Record<string, string | undefined>)

    return breadcrumbTrail(routes, current.routeId, params, frame.title).map((crumb) => ({
      href: crumb.to ?? '',
      label: crumb.title,
      isClickable: crumb.to !== null,
    }))
  }, [router, matches, frame.title, frame.workspaceId])

  return <BreadcrumbsBase items={items} />
}

type BreadcrumbsItem = {
  href: string
  label: React.ReactNode
  isClickable: boolean
}
interface BreadcrumbsBaseProps {
  items: BreadcrumbsItem[]
}

const BreadcrumbsBase = ({ items }: BreadcrumbsBaseProps) => {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <BreadcrumbItem key={index}>
              <Suspense fallback={<Skeleton className="h-4 w-32" />}>
                {item.isClickable ? (
                  <Link to={item.href} className="breadcrumb-link">
                    {item.label}
                  </Link>
                ) : (
                  <span className="breadcrumb-link">{item.label}</span>
                )}
              </Suspense>
            </BreadcrumbItem>
            {index < items.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
